/**
 * A debounce function that limits the rate at which a given function is called.
 * It ensures that the function `func` is only executed after a specified amount of time
 * has passed since the last time it was invoked. This is useful for scenarios like user
 * input or window resizing events where you want to limit the number of times a function
 * is executed in a short period.
 *
 * @param func - The function to be debounced. It will be called only after the specified timeout.
 * @param timeout - The time in milliseconds to wait before executing `func`.
 *                  If the function is called again within this period, the timer resets.
 * @returns A new function that can be invoked. It will delay the execution of `func`.
 */
export function debounce<Params extends any[]>(
    func: (...args: Params) => any,
    timeout: number
): (...args: Params) => void {
    let timer: ReturnType<typeof setTimeout>;
    return (...args: Params) => {
        clearTimeout(timer);
        timer = setTimeout(() => {
            func(...args);
        }, timeout);
    };
}
